import type {
  CalibratedLatencySummary,
  LatencyBudgetMonitor,
  StandingLatencyVerdict,
  StreamHealthEvent,
} from '@playcastdotio/PeerCore';
import type { DeepeningBlock } from './RecoveryEntryManager';

export type LatencyBudgetActionDeps = {
  latencyBudget: LatencyBudgetMonitor;
  getCalibratedLatency?: (peerId: string) => Pick<CalibratedLatencySummary, 'lastMs' | 'floorMs'> | undefined;
  getStandingLatency?: (peerId: string) => StandingLatencyVerdict | undefined;
  deepeningBlock: (peerId: string) => DeepeningBlock | null;
  emitHealth: (peerId: string, event: StreamHealthEvent) => void;
  /** The latency-first correction: a soft cut that then holds, sticky (D1). */
  correct: (peerId: string) => void;
  debug: (tag: string, line: Record<string, unknown>) => void;
  nowMs: () => number;
};

/**
 * LatencyBudgetActionManager — PRODUCT DECISION D2, the monitor's events turned into records
 * and one correction.
 *
 * >100 ms glass-to-glass is a bug, not a slowdown — but only the EXCESS above the link's own
 * floor is something a cut can fix. A sustained breach records the bug-class event once per
 * episode and drives the latency-first correction (the owner's `correct`). A floor over budget
 * is distance, or a standing buffer — the event carries the detector's `kind` — and is only
 * recorded: nothing the encoder does changes propagation, and cutting cannot drain a buffer.
 *
 * The event never waits; the correction does. While the settle or standing-buffer gate holds,
 * the breach is remembered and acted on at the first later tick that is still in breach and
 * no longer gated, so a breach that began during startup is not lost with the edge.
 */
export class LatencyBudgetActionManager {
  /** Peers whose breach was recorded but whose correction is still gated. */
  private readonly deferred = new Set<string>();

  constructor(private readonly deps: LatencyBudgetActionDeps) {}

  /** One guest tick: feed the monitor, record its edges, act when the gates allow. */
  public observe(peerId: string): void {
    const monitor = this.deps.latencyBudget;
    const reading = this.deps.getCalibratedLatency?.(peerId);
    monitor.observe(peerId, {
      atMs: this.deps.nowMs(),
      calibratedMs: reading?.lastMs ?? undefined,
      floorMs: reading?.floorMs ?? undefined,
      standingLatencyKind: this.deps.getStandingLatency?.(peerId)?.kind,
    });
    const floor = monitor.pollFloorEvent(peerId);
    if (floor) {
      this.deps.emitHealth(peerId, {
        kind: 'latency-floor-over-budget',
        stream: 'video',
        atMs: floor.atMs,
        detail: { floorMs: Math.round(floor.floorMs), kind: floor.kind },
      });
    }
    const breach = monitor.pollBreachEvent(peerId);
    if (breach) {
      this.deps.emitHealth(peerId, {
        kind: 'latency-budget-breach',
        stream: 'video',
        atMs: breach.atMs,
        detail: { worstLatencyMs: Math.round(breach.worstLatencyMs) },
      });
      this.deferred.add(peerId);
    }
    if (!this.deferred.has(peerId)) return;
    if (monitor.verdictFor(peerId) !== 'breach') {
      this.deferred.delete(peerId);
      return;
    }
    const block = this.deps.deepeningBlock(peerId);
    if (block) {
      this.deps.debug('[Recovery.budget]', { peerId, outcome: 'deferred', blocked: block });
      return;
    }
    this.deferred.delete(peerId);
    this.deps.correct(peerId);
  }

  public clearPeer(peerId: string): void {
    this.deferred.delete(peerId);
  }

  public clearAll(): void {
    this.deferred.clear();
  }
}
